import { useEffect, useRef, useState } from 'react'
import api from '../../../helpers/apiRequester'
import { Button, Field, Input, Spinner, StatusPill } from '../../../ui'
import { ClaimSearchResult, formatDate } from './shared'
import styles from './styles.module.scss'

/**
 * Comms management: find the client/claim an email belongs to. Searches every
 * claim we hold (not just the ones we have written to) by claim ref, client
 * name, email, postcode, agreement number or vehicle registration. A full
 * claim reference we do not hold yet is looked up in the introducer databases.
 */
export const ClaimSearch = ({ busy, onLink, onOpenClaim, actionLabel = 'Link' }: { busy: boolean; onLink: (claimRef: string) => void; onOpenClaim?: (claimRef: string) => void; actionLabel?: string }) => {
    const [q, setQ] = useState('')
    const [results, setResults] = useState<ClaimSearchResult[] | null>(null)
    const [searching, setSearching] = useState(false)
    const [lookedUp, setLookedUp] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const timer = useRef<number | null>(null)
    const lastQuery = useRef('')

    const search = async (term: string) => {
        const t = term.trim()
        if (t.length < 2) {
            setResults(null)
            return
        }
        lastQuery.current = t
        setSearching(true)
        setError(null)
        try {
            const resp = await api({ url: 'comms/inbox/claim-search', data: { q: t, limit: 12 } })
            if (lastQuery.current !== t) return
            if (resp.data?.success) {
                setResults(resp.data.results || [])
                setLookedUp(Boolean(resp.data.lookedUp))
            } else setError(resp.data?.message || 'Search failed.')
        } catch (err: any) {
            if (lastQuery.current === t) setError(err?.response?.data?.message || 'Search failed.')
        } finally {
            if (lastQuery.current === t) setSearching(false)
        }
    }

    useEffect(() => {
        if (timer.current) window.clearTimeout(timer.current)
        timer.current = window.setTimeout(() => search(q), 350)
        return () => {
            if (timer.current) window.clearTimeout(timer.current)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q])

    return (
        <div>
            <form
                className={styles.classRow}
                onSubmit={(e) => {
                    e.preventDefault()
                    search(q)
                }}
            >
                <Field label="Find the client or claim" hint="Claim ref, client name, email, postcode, agreement number or vehicle registration" className={styles.grow}>
                    {(id) => <Input id={id} small placeholder="e.g. pcpLC_1756873878_f3ace, Smith, M1 1AE…" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />}
                </Field>
                <Button type="submit" size="sm" variant="neutral" disabled={busy || q.trim().length < 2}>
                    Search
                </Button>
            </form>
            {searching && <Spinner block text="Searching claims…" />}
            {error && <p className={styles.mutedInline}>{error}</p>}
            {!searching && results && results.length === 0 && (
                <p className={styles.mutedInline}>
                    No claim matched “{q.trim()}”.{lookedUp ? ' The introducer databases were checked too.' : ' For a full claim reference, the introducer databases are checked as well.'}
                </p>
            )}
            {!searching && results && results.length > 0 && (
                <div>
                    {results.map((r) => (
                        <div key={r.claimRef} className={styles.claimResult}>
                            <div>
                                <div>
                                    <strong>{r.clientName || 'Unnamed client'}</strong> <span className={styles.mutedInline}>· {r.lenderName || 'lender unknown'}</span>
                                </div>
                                <div className={styles.claimResultMeta}>
                                    {onOpenClaim ? (
                                        <Button size="sm" variant="link" className={styles.mono} onClick={() => onOpenClaim(r.claimRef)} title="Open this claim to check before linking">
                                            {r.claimRef}
                                        </Button>
                                    ) : (
                                        <span className={styles.mono}>{r.claimRef}</span>
                                    )}
                                    {r.introducer && <span className={styles.mutedInline}>{r.introducer}</span>}
                                    {r.clientPostcode && <span className={styles.mutedInline}>{r.clientPostcode}</span>}
                                    {r.clientEmail && <span className={styles.mutedInline}>{r.clientEmail}</span>}
                                    {r.agreementNumber && <span className={styles.mutedInline}>agreement {r.agreementNumber}</span>}
                                    {r.vehicleRegistration && <span className={styles.mutedInline}>{r.vehicleRegistration}</span>}
                                    <StatusPill size="sm" tone={r.letterCount ? 'neutral' : 'warn'}>
                                        {r.letterCount ? `${r.letterCount} letter${r.letterCount === 1 ? '' : 's'} sent${r.lastLetterAt ? `, last ${formatDate(r.lastLetterAt)}` : ''}` : 'no letter sent yet'}
                                    </StatusPill>
                                </div>
                            </div>
                            <Button size="sm" variant="primary" disabled={busy} onClick={() => onLink(r.claimRef)}>
                                {actionLabel}
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
